import React, { useState, useEffect, useRef } from "react";
import { ArrowDown, EyeIcon, Info } from "lucide-react";
import { getRequest } from "../../service/index";
import { getEmployeeId, getEmployeeData } from "../../utils/localStorage";
import { nowInUserTz, parseToUserTz, toDateKey as toUserDateKey, formatTime as formatUserTime, dayjs } from "../../utils/dateTime";

import { resolveRelaxableRuleType } from "../../modals/AttendanceRules";

const PaySlip = (props) => {
  const { attendanceData, salaryStatus } = props;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [shiftData, setShiftData] = useState(null);
  const [employeeData, setEmployeeData] = useState(null);
  const [taxAndBonusData, setTaxAndBonusData] = useState(null);
  const [loanData, setLoanData] = useState(null);
  const [processedAttendance, setProcessedAttendance] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lateCount, setLateCount] = useState(0);
  const [earlyOutCount, setEarlyOutCount] = useState(0);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [activeTab, setActiveTab] = useState('current');
  const [previousMonthData, setPreviousMonthData] = useState(null);
  const [previousMonthLoading, setPreviousMonthLoading] = useState(false);
  const userData = getEmployeeData();
  const employeeId = getEmployeeId();
  const pdfRef = useRef(null);

  const getCurrentDate = () => nowInUserTz();

  const parseDate = (dateString) => parseToUserTz(dateString);

  const toDateKey = (dateString) => toUserDateKey(dateString) || '';

  const formatTime = (timeString) => {
    if (!timeString || timeString === '-') return '-';
    if (/^\d{1,2}:\d{2}(:\d{2})?$/.test(timeString)) {
      const [hours, minutes] = timeString.split(':');
      return `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}`;
    }
    return formatUserTime(timeString);
  };

  // Helper function to format date
  const formatDate = (date, format = 'YYYY-MM-DD') => {
    if (!date) return '';
    const parsed = parseDate(date);
    return parsed?.isValid() ? parsed.format(format) : '';
  };

  // Get month dates based on salary status
  const getMonthDates = () => {
    let targetDate;
    
    if (salaryStatus) {
      // If previous month salary is dispatched, show current month
      targetDate = getCurrentDate();
    } else {
      // If previous month salary is not dispatched, show previous month
      targetDate = getCurrentDate().subtract(1, 'month');
    }
    
    const startOfMonth = targetDate.startOf('month');
    const endOfMonth = targetDate.endOf('month');
    const daysInMonth = endOfMonth.date();
    
    const dates = [];
    for (let i = 1; i <= daysInMonth; i++) {
      dates.push(startOfMonth.date(i).format('YYYY-MM-DD'));
    }
    return dates;
  };

  // Get effective month dates considering joining date
  const getEffectiveMonthDates = () => {
    const monthDates = getMonthDates();
    
    // If no employee data or joining date, return all month dates
    if (!employeeData?.joining_date) return monthDates;
    
    const joiningDate = parseDate(employeeData.joining_date);
    const targetMonth = salaryStatus ? getCurrentDate() : getCurrentDate().subtract(1, 'month');
    
    if (!joiningDate?.isValid()) return monthDates;
    
    // Check if joining date is in the target month
    if (joiningDate.isSame(targetMonth, 'month') && joiningDate.isSame(targetMonth, 'year')) {
      // Employee joined in this month, filter dates from joining date onwards
      const joiningDay = joiningDate.date();
      return monthDates.filter(date => {
        const day = parseDate(date)?.date();
        return day >= joiningDay;
      });
    }
    
    // If joining date is before target month, return all dates
    if (joiningDate.isBefore(targetMonth, 'month')) {
      return monthDates;
    }
    
    // If joining date is after target month, return empty array (no salary)
    return [];
  };

  // Get current month dates (1st to last day of current month) - for backward compatibility
  const getCurrentMonthDates = () => {
    return getMonthDates();
  };

  // Get target month for display
  const getTargetMonth = () => {
    return salaryStatus ? getCurrentDate().format('MMMM YYYY') : getCurrentDate().subtract(1, 'month').format('MMMM YYYY');
  };

  // Get previous month name and year
  const getPreviousMonthInfo = () => {
    const now = getCurrentDate();
    const previousMonth = now.month() === 0 ? 11 : now.month() - 1;
    const previousYear = now.month() === 0 ? now.year() - 1 : now.year();
    return { month: previousMonth, year: previousYear, name: dayjs().month(previousMonth).format('MMMM YYYY') };
  };

  // Fetch employee data
  const fetchEmployeeData = async () => {
    try {
      setLoading(true);
      const response = await getRequest(`/api/employees/${employeeId}`);
      if (response?.employee) {
        setEmployeeData(response.employee);
      }
    } catch (error) {
      console.error("Error fetching employee data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch shift data for employee
  const fetchShiftData = async () => {
    try {
      setLoading(true);
      const response = await getRequest(`/api/companies/shifts/byEmployeeId?employeeId=${employeeId}`);
      if (response?.shift) {
        setShiftData(response.shift);
      }
    } catch (error) {
      console.error("Error fetching shift data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch tax and bonus data
  const fetchTaxAndBonusData = async () => {
    try {
      setLoading(true);
      const response = await getRequest(`/api/taxandbonus?employeeId=${employeeId}`);
      if (response?.entries) {
        setTaxAndBonusData(response.entries);
      }
    } catch (error) {
      console.error("Error fetching tax and bonus data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch loan data for employee
  const fetchLoanData = async () => {
    try {
      setLoading(true);
      const response = await getRequest(`/api/loanpayment/byEmployee?employeeId=${employeeId}&status=active`);
      if (response?.success && response?.data) {
        setLoanData(response.data);
      }
    } catch (error) {
      console.error("Error fetching loan data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch previous month salary data
  const fetchPreviousMonthData = async () => {
    try {
      setPreviousMonthLoading(true);
      const { month: previousMonth, year: previousYear } = getPreviousMonthInfo();
      
      const response = await getRequest(
        `/api/monthlysalery/byEmployee/${employeeId}?month=${previousMonth + 1}&year=${previousYear}`
      );
      
      if (response?.success && response?.data?.payrollRecords?.length > 0) {
        setPreviousMonthData(response.data);
      } else {
        setPreviousMonthData(null);
      }
    } catch (error) {
      console.error("Error fetching previous month data:", error);
      setPreviousMonthData(null);
    } finally {
      setPreviousMonthLoading(false);
    }
  };

  // Check if date is a shift working day
  const isShiftWorkingDay = (date) => {
    const parsed = parseDate(date);
    if (!parsed?.isValid()) return false;

    const dayName = parsed.format('dddd');

    if (shiftData?.workingDays?.length) {
      return shiftData.workingDays.includes(dayName);
    }

    // Fallback when shift data is unavailable
    return dayName !== 'Saturday' && dayName !== 'Sunday';
  };

  // Check if date is a calendar weekend (Sat/Sun)
  const isCalendarWeekend = (date) => {
    const parsed = parseDate(date);
    if (!parsed?.isValid()) return false;

    const dayName = parsed.format('dddd');
    return dayName === 'Saturday' || dayName === 'Sunday';
  };

  // Whether salary/off-day logic should follow shift workingDays
  const usesShiftWorkingDayCalculation = () => employeeData?.day_calculation === 'working';

  // Attendance is required on shift working days for all day_calculation modes
  const isAttendanceRequiredDay = (date) => isShiftWorkingDay(date);

  // Off days depend on day_calculation setting
  const isOffDay = (date) => {
    if (usesShiftWorkingDayCalculation()) {
      return !isShiftWorkingDay(date);
    }

    // total / monthly / fixed: keep previous Sat/Sun weekend logic
    return isCalendarWeekend(date);
  };

  // Count working days in a date range (used for day_calculation: working)
  const countWorkingDays = (dates = getMonthDates()) => {
    if (!dates?.length) return 0;
    return dates.filter(date => isShiftWorkingDay(date)).length;
  };

  // Get divisor count for per-day salary based on day_calculation
  const getDayCalculationDivisor = () => {
    const { day_calculation, fixed_Days } = employeeData || {};
    const monthDates = getMonthDates();

    switch (day_calculation) {
      case 'working':
        return countWorkingDays(monthDates);
      case 'fixed':
        return fixed_Days && fixed_Days > 0 ? fixed_Days : monthDates.length;
      case 'monthly':
      case 'total':
      default:
        return monthDates.length;
    }
  };

  // Calculate per-day salary based on day_calculation
  const calculatePerDaySalary = () => {
    if (!employeeData?.monthly_salary) return 0;

    const divisor = getDayCalculationDivisor();
    if (!divisor) return 0;

    return employeeData.monthly_salary / divisor;
  };

  // Calculate proportional salary based on joining date and day_calculation
  const calculateProportionalSalary = () => {
    if (!employeeData?.monthly_salary) return 0;
    
    const effectiveDates = getEffectiveMonthDates();
    const totalMonthDates = getMonthDates();
    
    if (effectiveDates.length === 0) return 0;

    const { day_calculation, fixed_Days, monthly_salary } = employeeData;

    // Full month — return full salary for all modes
    if (effectiveDates.length === totalMonthDates.length) {
      return monthly_salary;
    }

    switch (day_calculation) {
      case 'working': {
        const workingDaysInMonth = countWorkingDays(totalMonthDates);
        const workingDaysInPeriod = countWorkingDays(effectiveDates);

        if (workingDaysInMonth === 0) return 0;
        return (monthly_salary / workingDaysInMonth) * workingDaysInPeriod;
      }

      case 'fixed': {
        if (fixed_Days && fixed_Days > 0) {
          const proportion = effectiveDates.length / totalMonthDates.length;
          return monthly_salary * proportion;
        }
        break;
      }

      case 'monthly':
      case 'total':
      default: {
        const proportion = effectiveDates.length / totalMonthDates.length;
        return monthly_salary * proportion;
      }
    }

    const proportion = effectiveDates.length / totalMonthDates.length;
    return monthly_salary * proportion;
  };

  // Calculate per-hour and per-minute salary
  const calculatePerHourAndMinuteSalary = (perDaySalary) => {
    const workingHours = shiftData?.workingHours || employeeData?.shift?.workingHours || 9;
    if (!workingHours) return { perHour: 0, perMinute: 0 };
    
    const perHourSalary = perDaySalary / workingHours;
    const perMinuteSalary = perHourSalary / 60;
    
    return { perHour: perHourSalary, perMinute: perMinuteSalary };
  };

  // Convert time string to minutes for easier comparison
  const timeToMinutes = (timeStr) => {
    if (!timeStr) return 0;
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours * 60 + minutes;
  };

  // Convert minutes to time string
  const minutesToTime = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
  };

  // Apply attendance rules and calculate status
  const applyAttendanceRules = (attendance, shift) => {
    if (!shift) return attendance;

    const processed = { ...attendance };
    const settings = shift.settings;

    // Calculate per-day salary
    const perDaySalary = calculatePerDaySalary();
    const { perMinute: perMinuteSalary } = calculatePerHourAndMinuteSalary(perDaySalary);

    // Convert times to minutes for comparison
    const startTimeMinutes = timeToMinutes(shift.startTime);
    const endTimeMinutes = timeToMinutes(shift.endTime);
    const checkInMinutes = timeToMinutes(attendance.checkIn);
    const checkOutMinutes = timeToMinutes(attendance.checkOut);

    // Handle overnight shifts
    let adjustedEndTimeMinutes = endTimeMinutes;
    let adjustedCheckOutMinutes = checkOutMinutes;

    if (shift.dayChange && endTimeMinutes < startTimeMinutes) {
      if (checkOutMinutes < startTimeMinutes) {
        adjustedCheckOutMinutes = checkOutMinutes + 24 * 60;
      }
      adjustedEndTimeMinutes = endTimeMinutes + 24 * 60;
    }

    const getWorkingMinutes = () => {
      let checkOut = adjustedCheckOutMinutes;
      const checkIn = checkInMinutes;
      if (shift.dayChange && checkOut < checkIn) {
        checkOut += 24 * 60;
      }
      return checkOut - checkIn;
    };

    // Relaxable settings replace attendance and early-out rules
    if (settings?.isRelaxableSettingsEnabled && settings.relaxable) {
      const ruleType = resolveRelaxableRuleType(settings.relaxable);
      const graceMinutes = Number(settings.relaxable.flexibleStartHours) || 0;
      const flexibleStartMinutes = startTimeMinutes + graceMinutes;

      processed.lateDeduction = 0;
      processed.incompleteDeduction = 0;
      processed.earlyOutDeduction = 0;
      processed.lateMinutes = 0;
      processed.incompleteMinutes = 0;

      if (ruleType === 'lateArrival') {
        if (checkInMinutes <= flexibleStartMinutes) {
          processed.status = 'Present';
        } else {
          processed.status = 'Late';
          processed.lateMinutes = checkInMinutes - startTimeMinutes;

          if (settings.relaxable.lateArrivalDeduction === 'quarter') {
            processed.lateDeduction = perDaySalary / 4;
          } else if (settings.relaxable.lateArrivalDeduction === 'half') {
            processed.lateDeduction = perDaySalary / 2;
          }
        }
      } else if (ruleType === 'detectHourly') {
        const workingMinutes = getWorkingMinutes();
        const requiredMinutes = (shift.workingHours ?? 9) * 60;

        if (workingMinutes >= requiredMinutes) {
          processed.status = 'Present';
        } else {
          processed.status = 'Incomplete';
          processed.incompleteMinutes = requiredMinutes - workingMinutes;
          processed.incompleteDeduction = processed.incompleteMinutes * perMinuteSalary;
        }
      } else {
        processed.status = 'Present';
      }

      processed.perDaySelery = perDaySalary;
      return processed;
    }

    // Apply Attendance Rules (Late Rules)
    if (settings?.isAttendanceRulesEnabled) {
      if (settings.lateRuleType === 'time') {
        // Rule 2: Time-based late detection
        const lateThresholdMinutes = startTimeMinutes + settings.rule2.lateMinutes;
        
        if (checkInMinutes > lateThresholdMinutes) {
          processed.status = 'Late';
          processed.lateMinutes = checkInMinutes - startTimeMinutes;
          
          // Calculate late deduction based on lateTimeDeduction
          const deductionMultiplier = {
            'single': 1,
            '1.5': 1.5,
            'double': 2,
            'triple': 3
          }[settings.rule2.lateTimeDeduction] || 1;
          
          // Deduct based on per-minute salary
          processed.lateDeduction = processed.lateMinutes * perMinuteSalary * deductionMultiplier;
        } else {
          processed.status = 'Present';
        }
      } else if (settings.lateRuleType === 'count') {
        // Rule 1: Count-based late detection
        const graceMinutes = Number(settings.rule1.lateMinutes) || 0;
        const lateThresholdMinutes = startTimeMinutes + graceMinutes;

        if (checkInMinutes > lateThresholdMinutes) {
          processed.status = 'Late';
          processed.lateMinutes = checkInMinutes - startTimeMinutes;
          processed.lateDeduction = 0;
          processed.excludeFromLateCount = false;

          if (settings.rule1.isLateMinutesDeductionEnabled) {
            const minutesLateThreshold = Number(settings.rule1.lateMinutesThreshold) || 0;

            if (processed.lateMinutes >= minutesLateThreshold) {
              processed.excludeFromLateCount = true;
              const deductionType = settings.rule1.lateMinutesDeduction;

              if (deductionType === 'half') {
                processed.lateDeduction = perDaySalary / 2;
              } else if (deductionType === 'full') {
                processed.lateDeduction = perDaySalary;
              }
            }
          }
        } else {
          processed.status = 'Present';
        }
      }
    }

    // Apply Early Out Rules
    if (settings?.isEarlyOutRulesEnabled) {
      if (settings.earlyOutRuleType === 'time') {
        // Rule 2: Time-based early out detection
        const graceMinutes = Number(settings.earlyOutRule2.earlyOutMinutes) || 0;
        const earlyOutThresholdMinutes = adjustedEndTimeMinutes - graceMinutes;
        
        if (adjustedCheckOutMinutes < earlyOutThresholdMinutes) {
          processed.status = processed.status === 'Late' ? 'Late & Early Out' : 'Early Out';
          processed.earlyOutMinutes = adjustedEndTimeMinutes - adjustedCheckOutMinutes;
          
          // Calculate early out deduction based on earlyOutTimeDeduction
          const deductionMultiplier = {
            '1': 1,
            '1.5': 1.5,
            '2': 2,
            '2.5': 2.5,
            '3': 3
          }[settings.earlyOutRule2.earlyOutTimeDeduction] || 1;
          
          // Deduct based on per-minute salary
          processed.earlyOutDeduction = processed.earlyOutMinutes * perMinuteSalary * deductionMultiplier;
        }
      } else if (settings.earlyOutRuleType === 'count') {
        // Rule 1: Count-based early out detection
        const graceMinutes = Number(settings.earlyOutRule1.earlyOutMinutes) || 0;
        const earlyOutThresholdMinutes = adjustedEndTimeMinutes - graceMinutes;
        
        if (adjustedCheckOutMinutes < earlyOutThresholdMinutes) {
          processed.status = processed.status === 'Late' ? 'Late & Early Out' : 'Early Out';
          processed.earlyOutMinutes = adjustedEndTimeMinutes - adjustedCheckOutMinutes;
          // For count-based, no immediate deduction - will be calculated based on count
          processed.earlyOutDeduction = 0;
        }
      }
    }

    // Set per-day salary based on calculated value
    processed.perDaySelery = perDaySalary;

    return processed;
  };

  // Process attendance data for current month
  const processAttendanceData = () => {
    if (!shiftData || !attendanceData) return;

    const currentMonthDates = getEffectiveMonthDates();
    const processed = [];

    // Count late and early out occurrences for count-based rules
    let lateCount = 0;
    let earlyOutCount = 0;
    const lateRecords = [];
    const earlyOutRecords = [];

    // First pass: process all records and count lates and early outs
    currentMonthDates.forEach(date => {
      // Find attendance record for this date
      const attendanceRecord = attendanceData.find(record => 
        toDateKey(record.date) === date
      );

      if (attendanceRecord) {
        // Check if the status is already set to "Leave" or "Holiday"
        if (attendanceRecord.status === 'Leave' || attendanceRecord.status === 'Holiday') {
          // Keep the original status and calculate salary accordingly
          const leaveHolidayRecord = {
            ...attendanceRecord,
            date: toDateKey(attendanceRecord.date) || date,
            workingHour: '0:00',
            overtime: '0:00',
            perDaySelery: attendanceRecord.status === 'Leave' ? calculatePerDaySalary() : 0,
            overtimeSalery: 0
          };
          processed.push(leaveHolidayRecord);
        }
        // Check if Check In or Check Out is missing
        else if (!attendanceRecord.checkIn || !attendanceRecord.checkOut || 
            attendanceRecord.checkIn === '-' || attendanceRecord.checkOut === '-' ||
            attendanceRecord.checkIn === '' || attendanceRecord.checkOut === '') {
          // Mark as missing if either Check In or Check Out is missing
          const missingRecord = {
            ...attendanceRecord,
            date: toDateKey(attendanceRecord.date) || date,
            status: 'Missing',
            workingHour: '0:00',
            overtime: '0:00',
            perDaySelery: calculatePerDaySalary(),
            overtimeSalery: 0
          };
          processed.push(missingRecord);
        } else {
          // Apply rules to existing attendance
          const processedRecord = applyAttendanceRules({
            ...attendanceRecord,
            date: toDateKey(attendanceRecord.date) || date
          }, shiftData);
          
          // Count late records for count-based rules (exclude threshold-based lates)
          if (processedRecord.status === 'Late' || processedRecord.status === 'Late & Early Out') {
            if (!processedRecord.excludeFromLateCount) {
              lateCount++;
              lateRecords.push(processedRecord);
            }
          }
          
          // Count early out records for count-based rules
          if (processedRecord.status === 'Early Out' || processedRecord.status === 'Late & Early Out') {
            earlyOutCount++;
            earlyOutRecords.push(processedRecord);
          }
          
          processed.push(processedRecord);
        }
      } else if (isAttendanceRequiredDay(date)) {
        // Create missing record for shift working day
        const missingRecord = {
          date: date,
          status: 'Missing',
          workingHour: '0:00',
          overtime: '0:00',
          perDaySelery: calculatePerDaySalary(),
          overtimeSalery: 0
        };
        processed.push(missingRecord);
      } else if (isOffDay(date)) {
        // Off day — shift off days for 'working', Sat/Sun for other modes
        const weekendRecord = {
          date: date,
          status: 'Weekend',
          workingHour: '0:00',
          overtime: '0:00',
          perDaySelery: 0,
          overtimeSalery: 0
        };
        processed.push(weekendRecord);
      }
    });

    // Second pass: apply count-based deductions if needed
    if (shiftData?.settings?.isAttendanceRulesEnabled && 
        shiftData.settings.lateRuleType === 'count' && 
        lateCount >= shiftData.settings.rule1.lateCount) {
      
      const perDaySalary = calculatePerDaySalary();
      const deductionType = shiftData.settings.rule1.lateDeduction;
      const lateCountThreshold = shiftData.settings.rule1.lateCount;
      
      // Calculate how many times the threshold is reached
      const deductionTimes = Math.floor(lateCount / lateCountThreshold);
      
      // Calculate deduction amount based on type
      let deductionAmount = 0;
      if (deductionType === 'half') {
        deductionAmount = perDaySalary / 2;
      } else if (deductionType === 'full') {
        deductionAmount = perDaySalary;
      }
      
      // Calculate total deduction to distribute
      const totalDeduction = deductionAmount * deductionTimes;
      
      // Distribute deduction among eligible late records (exclude threshold-based lates)
      let deductionApplied = 0;
      processed.forEach(record => {
        if (
          (record.status === 'Late' || record.status === 'Late & Early Out') &&
          !record.excludeFromLateCount &&
          !record.lateDeduction &&
          deductionApplied < totalDeduction
        ) {
          record.lateDeduction = deductionAmount;
          deductionApplied += deductionAmount;
        }
      });
    }

    // Apply count-based early out deductions if needed
    if (shiftData?.settings?.isEarlyOutRulesEnabled && 
        shiftData.settings.earlyOutRuleType === 'count' &&
        earlyOutCount >= shiftData.settings.earlyOutRule1.earlyOutCount) {
      
      const perDaySalary = calculatePerDaySalary();
      const deductionType = shiftData.settings.earlyOutRule1.earlyOutDeduction;
      const earlyOutCountThreshold = shiftData.settings.earlyOutRule1.earlyOutCount;
      
      // Calculate how many times the threshold is reached
      const deductionTimes = Math.floor(earlyOutCount / earlyOutCountThreshold);
      
      // Calculate deduction amount based on type
      let deductionAmount = 0;
      if (deductionType === 'half') {
        deductionAmount = perDaySalary / 2;
      } else if (deductionType === 'full') {
        deductionAmount = perDaySalary;
      }
      
      // Calculate total deduction to distribute
      const totalDeduction = deductionAmount * deductionTimes;
      
      // Distribute deduction among early out records (first early out records get the deduction)
      let deductionApplied = 0;
      processed.forEach(record => {
        if ((record.status === 'Early Out' || record.status === 'Late & Early Out') && 
            deductionApplied < totalDeduction) {
          record.earlyOutDeduction = deductionAmount;
          deductionApplied += deductionAmount;
        }
      });
    }

    setProcessedAttendance(processed);
  };

  // Calculate payroll totals based on processed attendance
  const calculatePayroll = () => {
    if (!processedAttendance || processedAttendance.length === 0) return null;

    const totals = {
      totalDays: processedAttendance.length,
      presentDays: 0,
      missingDays: 0,
      absentDays: 0,
      halfDays: 0,
      leaveDays: 0,
      holidayDays: 0,
      lateDays: 0,
      earlyOutDays: 0,
      incompleteDays: 0,
      weekendDays: 0,
      totalSalary: 0,
      totalOvertimeSalary: 0,
      totalWorkingHours: 0,
      totalOvertime: 0,
      totalDeductions: 0
    };

    processedAttendance.forEach(day => {
      // Count days by status
      switch (day.status?.toLowerCase()) {
        case 'present':
          totals.presentDays++;
          if (day.perDaySelery) {
            totals.totalSalary += parseFloat(day.perDaySelery) || 0;
          }
          break;
        case 'missing':
          totals.missingDays++;
          break;
        case 'absent':
          totals.absentDays++;
          break;
        case 'late':
          totals.lateDays++;
          if (day.perDaySelery) {
            totals.totalSalary += parseFloat(day.perDaySelery) || 0;
          }
          if (day.lateDeduction) {
            totals.totalDeductions += day.lateDeduction;
          }
          break;
        case 'late & early out':
          totals.lateDays++;
          if (day.perDaySelery) {
            totals.totalSalary += parseFloat(day.perDaySelery) || 0;
          }
          if (day.lateDeduction) {
            totals.totalDeductions += day.lateDeduction;
          }
          if (day.earlyOutDeduction) {
            totals.totalDeductions += day.earlyOutDeduction;
          }
          break;
        case 'early out':
          totals.earlyOutDays++;
          if (day.perDaySelery) {
            totals.totalSalary += parseFloat(day.perDaySelery) || 0;
          }
          if (day.earlyOutDeduction) {
            totals.totalDeductions += day.earlyOutDeduction;
          }
          break;
        case 'incomplete':
          totals.incompleteDays++;
          if (day.perDaySelery) {
            totals.totalSalary += parseFloat(day.perDaySelery) || 0;
          }
          if (day.incompleteDeduction) {
            totals.totalDeductions += day.incompleteDeduction;
          }
          break;
        case 'leave':
          totals.leaveDays++;
          if (day.perDaySelery) {
            totals.totalSalary += parseFloat(day.perDaySelery) || 0;
          }
          break;
        case 'holiday':
          totals.holidayDays++;
          break;
        case 'half day':
        case 'halfday':
          totals.halfDays++;
          if (day.perDaySelery) {
            totals.totalSalary += (parseFloat(day.perDaySelery) || 0) / 2;
          }
          break;
        case 'weekend':
          totals.weekendDays++;
          break;
        default:
          if (day.workingHour && day.workingHour !== "0:00") {
            totals.presentDays++;
            if (day.perDaySelery) {
              totals.totalSalary += parseFloat(day.perDaySelery) || 0;
            }
          }
      }

      // Calculate overtime salary (only if approved)
      if (day.overtimeSalery) {
        totals.totalOvertimeSalary += parseFloat(day.overtimeSalery) || 0;
      }

      // Calculate working hours
      if (day.workingHour && day.workingHour !== "0:00") {
        if (typeof day.workingHour === 'string' && day.workingHour.includes(':')) {
          const [hours, minutes] = day.workingHour.split(':').map(Number);
          totals.totalWorkingHours += hours + (minutes / 60);
        } else {
          totals.totalWorkingHours += parseFloat(day.workingHour) || 0;
        }
      }

      // Calculate overtime hours
      if (day.overtime && day.overtime !== "0:00") {
        if (typeof day.overtime === 'string' && day.overtime.includes(':')) {
          const [hours, minutes] = day.overtime.split(':').map(Number);
          totals.totalOvertime += hours + (minutes / 60);
        } else {
          totals.totalOvertime += parseFloat(day.overtime) || 0;
        }
      }
    });

    return totals;
  };

  useEffect(() => {
    fetchEmployeeData();
    fetchShiftData();
    fetchTaxAndBonusData();
    fetchLoanData();
    fetchPreviousMonthData();
  }, []);

  useEffect(() => {
    if (shiftData && employeeData) {
      processAttendanceData();
    }
  }, [shiftData, employeeData, attendanceData, taxAndBonusData, loanData]);

  const handleViewClick = () => {
    setIsModalOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    document.body.style.overflow = 'unset';
  };

  const handleDetailsClick = () => {
    setDetailsModalOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const handleCloseDetailsModal = () => {
    setDetailsModalOpen(false);
    document.body.style.overflow = 'unset';
  };

  // PDF Download Function
  const handleDownloadPDF = async () => {
    try {
      setIsGeneratingPDF(true);
      
      const jsPDF = (await import('jspdf')).default;
      const html2canvas = (await import('html2canvas')).default;
      
      if (!pdfRef.current) {
        console.error('PDF reference not found');
        return;
      }

      const tempContainer = document.createElement('div');
      tempContainer.style.position = 'absolute';
      tempContainer.style.left = '-9999px';
      tempContainer.style.top = '0';
      tempContainer.style.width = '800px';
      tempContainer.style.backgroundColor = 'white';
      tempContainer.style.padding = '20px';
      tempContainer.style.fontFamily = 'Arial, sans-serif';
      document.body.appendChild(tempContainer);

      const pdfContent = generatePDFContent();
      tempContainer.innerHTML = pdfContent;

      await new Promise(resolve => setTimeout(resolve, 1000));

      const canvas = await html2canvas(tempContainer, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        width: 800,
        height: tempContainer.scrollHeight
      });

      document.body.removeChild(tempContainer);

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      const imgWidth = 210;
      const pageHeight = 295;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      const fileName = `payslip_${employeeData?.firstName || 'employee'}_${getTargetMonth().replace(' ', '_')}.pdf`;
      pdf.save(fileName);

    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  // PDF Download Function for Previous Month
  const handleDownloadPreviousMonthPDF = async () => {
    try {
      setIsGeneratingPDF(true);
      
      const jsPDF = (await import('jspdf')).default;
      const html2canvas = (await import('html2canvas')).default;
      
      const tempContainer = document.createElement('div');
      tempContainer.style.position = 'absolute';
      tempContainer.style.left = '-9999px';
      tempContainer.style.top = '0';
      tempContainer.style.width = '800px';
      tempContainer.style.backgroundColor = 'white';
      tempContainer.style.padding = '20px';
      tempContainer.style.fontFamily = 'Arial, sans-serif';
      document.body.appendChild(tempContainer);

      const pdfContent = generatePreviousMonthPDFContent();
      tempContainer.innerHTML = pdfContent;

      await new Promise(resolve => setTimeout(resolve, 1000));

      const canvas = await html2canvas(tempContainer, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        width: 800,
        height: tempContainer.scrollHeight
      });

      document.body.removeChild(tempContainer);

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      const imgWidth = 210;
      const pageHeight = 295;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      const { name: monthName, year: previousYear } = getPreviousMonthInfo();
      const fileName = `payslip_${previousMonthData?.employeeDetails?.employeeName || 'employee'}_${monthName.replace(' ', '_')}_${previousYear}.pdf`;
      pdf.save(fileName);

    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  // Generate PDF content HTML for Previous Month
  const generatePreviousMonthPDFContent = () => {
    if (!previousMonthData || !previousMonthData.payrollRecords || previousMonthData.payrollRecords.length === 0) {
      return '<div>No previous month data available</div>';
    }

    const payrollRecord = previousMonthData.payrollRecords[0];
    const employeeDetails = previousMonthData.employeeDetails;
    const { name: monthName, year: previousYear } = getPreviousMonthInfo();

    return `
      <div style="font-family: Arial, sans-serif; max-width: 800px; margin: 0 auto; background: white;">
        <!-- Header -->
        <div style="display: flex; justify-content: space-between; align-items: center;text-align: center; margin-bottom: 30px; border-bottom: 3px solid #1e40af; padding-bottom: 20px;">
          ${userData?.companyLogo ? `
            <div style="margin-bottom: 15px;">
              <img src="${userData.companyLogo}" alt="Company Logo" style="max-height: 60px; max-width: 200px; object-fit: contain;" />
              <h2 style="color: #374151; margin: 5px 0; font-size: 18px;">${userData?.companyName || 'Company'}</h2>
            </div>
          ` : ''}
          <div style="margin-bottom: 15px;">
            <h1 style="color: #1e40af; margin: 0; font-size: 28px;">PAYSLIP</h1>
            <h3 style="color: #374151; margin: 10px 0; font-size: 16px;">${monthName}</h3>
           </div>
        </div>

        <!-- Employee Details -->
        <div style="background: #dbeafe; padding: 20px; border-radius: 8px; margin-bottom: 20px;">
          <h3 style="color: #1e40af; margin: 0 0 15px 0; font-size: 18px;">Employee Details</h3>
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 20px;">
            <div>
              <div style="margin-bottom: 8px;">
                <span style="font-weight: bold; color: #374151;">Employee Name:</span>
                <span style="margin-left: 10px; color: #1f2937;">${employeeDetails?.employeeName || 'N/A'}</span>
              </div>
              <div style="margin-bottom: 8px;">
                <span style="font-weight: bold; color: #374151;">Employee ID:</span>
                <span style="margin-left: 10px; color: #1f2937;">${employeeDetails?.employeeCode || 'N/A'}</span>
              </div>
              <div style="margin-bottom: 8px;">
                <span style="font-weight: bold; color: #374151;">Designation:</span>
                <span style="margin-left: 10px; color: #1f2937;">${employeeDetails?.designation || 'N/A'}</span>
              </div>
            </div>
            <div>
              <div style="margin-bottom: 8px;">
                <span style="font-weight: bold; color: #374151;">Department:</span>
                <span style="margin-left: 10px; color: #1f2937;">${employeeDetails?.department || 'N/A'}</span>
              </div>
              <div style="margin-bottom: 8px;">
                <span style="font-weight: bold; color: #374151;">Monthly Salary:</span>
                <span style="margin-left: 10px; color: #1f2937;">${formatCurrency(employeeDetails?.monthlySalary || 0)}</span>
              </div>
              <div style="margin-bottom: 8px;">
                <span style="font-weight: bold; color: #374151;">Status:</span>
                <span style="margin-left: 10px; color: #1f2937; text-transform: capitalize;">${payrollRecord?.status || 'N/A'}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Loan Information Section -->
        ${previousMonthData?.loans && previousMonthData.loans.length > 0 ? `
        <div style="background: #fef3c7; padding: 20px; border-radius: 8px; margin-bottom: 20px;">
          <h3 style="color: #92400e; margin: 0 0 15px 0; font-size: 18px;">Active Loans</h3>
          <div style="space-y: 15px;">
            ${previousMonthData.loans.map(loan => `
              <div style="background: white; padding: 15px; border-radius: 6px; border: 1px solid #fbbf24; margin-bottom: 15px;">
                <div style="display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 20px;">
                  <div>
                    <div style="margin-bottom: 8px;">
                      <span style="font-weight: bold; color: #374151;">Loan Date:</span>
                      <span style="margin-left: 10px; color: #1f2937;">${loan.loanDate ? parseDate(loan.loanDate).format('DD/MM/YYYY') : 'N/A'}</span>
                    </div>
                    <div style="margin-bottom: 8px;">
                      <span style="font-weight: bold; color: #374151;">Loan:</span>
                      <span style="margin-left: 10px; color: #1f2937;">${formatCurrency(loan.loanAmount || 0)}</span>
                    </div>
                  </div>
                  <div>
                    <div style="margin-bottom: 8px;">
                      <span style="font-weight: bold; color: #374151;">Installment:</span>
                      <span style="margin-left: 10px; color: #1f2937;">${formatCurrency(loan.perMonthInstalment || 0)}</span>
                    </div>
                    <div style="margin-bottom: 8px;">
                      <span style="font-weight: bold; color: #374151;">Paid:</span>
                      <span style="margin-left: 10px; color: #1f2937;">${formatCurrency(loan.totalPaid || 0)}</span>
                    </div>
                  </div>
                  <div>
                    <div style="margin-bottom: 8px;">
                      <span style="font-weight: bold; color: #374151;">Duration:</span>
                      <span style="margin-left: 10px; color: #1f2937;">${loan.numberOfMonths || 0} months</span>
                    </div>
                    <div style="margin-bottom: 8px;">
                      <span style="font-weight: bold; color: #374151;">Status:</span>
                      <span style="margin-left: 10px; color: #1f2937; text-transform: capitalize;">${loan.status || 'Active'}</span>
                    </div>
                    <div style="margin-bottom: 8px;">
                      <span style="font-weight: bold; color: #374151;">Remaining:</span>
                      <span style="margin-left: 10px; color: #dc2626; font-weight: bold;">${formatCurrency(loan.remainingBalance || 0)}</span>
                    </div>
                  </div>
                </div>
              </div>
            `).join('')}
          </div>
        </div>
        ` : ''}

        <!-- Payment Details -->
        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 20px;">
          <!-- Allowances -->
          <div style="background: #dcfce7; padding: 20px; border-radius: 8px;">
            <h3 style="color: #166534; margin: 0 0 15px 0; font-size: 18px;">PAYMENT & ALLOWANCES</h3>
            ${payrollRecord.allowances.map(allowance => `
              <div style="display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #bbf7d0;">
                <span style="color: #374151;">${allowance.name}</span>
                <span style="font-weight: bold; color: #166534;">${formatCurrency(allowance.amount)}</span>
              </div>
            `).join('')}
            <div style="display: flex; justify-content: space-between; padding: 12px 0; border-top: 2px solid #22c55e; margin-top: 10px;">
              <span style="font-weight: bold; color: #166534;">Total Allowances</span>
              <span style="font-size: 18px; font-weight: bold; color: #166534;">${formatCurrency(payrollRecord.totalAllowances)}</span>
            </div>
          </div>

          <!-- Deductions -->
          <div style="background: #fee2e2; padding: 20px; border-radius: 8px;">
            <h3 style="color: #dc2626; margin: 0 0 15px 0; font-size: 18px;">DEDUCTIONS</h3>
            ${payrollRecord.deductions.map(deduction => `
              <div style="display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #fecaca;">
                <span style="color: #374151;">${deduction.name}</span>
                <span style="font-weight: bold; color: #dc2626;">${formatCurrency(deduction.amount)}</span>
              </div>
            `).join('')}
            <div style="display: flex; justify-content: space-between; padding: 12px 0; border-top: 2px solid #ef4444; margin-top: 10px;">
              <span style="font-weight: bold; color: #dc2626;">Total Deductions</span>
              <span style="font-size: 18px; font-weight: bold; color: #dc2626;">${formatCurrency(payrollRecord.totalDeductions)}</span>
            </div>
          </div>
        </div>

        <!-- Net Salary -->
        <div style="background: #dbeafe; padding: 20px; border-radius: 8px; text-align: center;">
          <div style="display: flex; justify-content: space-between; align-items: center;">
            <span style="font-size: 20px; font-weight: bold; color: #1e40af;">NET SALARY</span>
            <span style="font-size: 28px; font-weight: bold; color: #1e40af;">${formatCurrency(payrollRecord.netSalary)}</span>
          </div>
        </div>

        <!-- Footer -->
        <div style="margin-top: 10px; text-align: center; color: #6b7280; font-size: 12px; border-top: 1px solid #e5e7eb; padding-top: 20px;">
          <p>Generated on ${getCurrentDate().format('DD/MM/YYYY HH:mm')}</p>
          <p>https://empellohrm.com</p>
          <p>Computer-generated — no signature/Stamp required. | Confidential — do not share.</p>
        </div>
      </div>
    `;
  };

  // Generate PDF content HTML
  const generatePDFContent = () => {
    const payrollTotals = calculatePayroll();
    const combinedLeaveDays = (payrollTotals?.leaveDays || 0) + (payrollTotals?.halfDays || 0);
    const combinedAbsentDays = (payrollTotals?.absentDays || 0) + (payrollTotals?.incompleteDays || 0);
    const { allowances: taxAndBonusAllowances, deductions: taxAndBonusDeductions } = calculateTaxAndBonusItems();
    const allowances = [
      { name: "Basic Salary", amount: calculateProportionalSalary() },
      { name: "Overtime Pay", amount: payrollTotals?.totalOvertimeSalary || 0 },
      ...taxAndBonusAllowances
    ];
    const attendanceDeductions = calculateAttendanceDeductions();
    const loanDeductions = calculateLoanDeductions();
    const incomeTax = calculateIncomeTax();
    const allDeductions = [...attendanceDeductions, ...loanDeductions, ...taxAndBonusDeductions];
    if (incomeTax > 0) {
      allDeductions.push({
        name: "Income Tax (Pakistan)",
        amount: incomeTax
      });
    }
    const deductions = allDeductions;
    const totalAllowances = allowances.reduce((sum, item) => sum + item.amount, 0);
    const totalDeductions = deductions.reduce((sum, item) => sum + item.amount, 0);
    const netSalary = totalAllowances - totalDeductions;

    return `
      <div style="font-family: Arial, sans-serif; max-width: 800px; margin: 0 auto; background: white;">
        <!-- Header -->
        <div style="display: flex; justify-content: space-between; align-items: center;text-align: center; margin-bottom: 30px; border-bottom: 3px solid #1e40af; padding-bottom: 20px;">
          ${userData?.companyLogo ? `
            <div style="margin-bottom: 15px;">
              <img src="${userData.companyLogo}" alt="Company Logo" style="max-height: 60px; max-width: 200px; object-fit: contain;" />
              <h2 style="color: #374151; margin: 5px 0; font-size: 18px;">${userData?.companyName || 'Company'}</h2>
            </div>
          ` : ''}
          <div style="margin-bottom: 15px;">
            <h1 style="color: #1e40af; margin: 0; font-size: 28px;">PAYSLIP</h1>
            <h3 style="color: #374151; margin: 10px 0; font-size: 16px;">${getTargetMonth()}</h3>
           </div>
        </div>

        <!-- Employee Details -->
        <div style="background: #dbeafe; padding: 20px; border-radius: 8px; margin-bottom: 20px;">
          <h3 style="color: #1e40af; margin: 0 0 15px 0; font-size: 18px;">Employee Details</h3>
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 20px;">
            <div>
              <div style="margin-bottom: 8px;">
                <span style="font-weight: bold; color: #374151;">Employee Name:</span>
                <span style="margin-left: 10px; color: #1f2937;">${employeeData ? `${employeeData.firstName} ${employeeData.lastName}` : 'Loading...'}</span>
              </div>
              <div style="margin-bottom: 8px;">
                <span style="font-weight: bold; color: #374151;">Employee ID:</span>
                <span style="margin-left: 10px; color: #1f2937;">${employeeData?.employee_code || 'Loading...'}</span>
              </div>
              <div style="margin-bottom: 8px;">
                <span style="font-weight: bold; color: #374151;">Join Date:</span>
                <span style="margin-left: 10px; color: #1f2937;">${employeeData?.joining_date ? parseDate(employeeData.joining_date).format('DD/MM/YYYY') : 'Loading...'}</span>
              </div>
            </div>
            <div>
              <div style="margin-bottom: 8px;">
                <span style="font-weight: bold; color: #374151;">Designation:</span>
                <span style="margin-left: 10px; color: #1f2937;">${employeeData?.designation?.designationName || 'Loading...'}</span>
              </div>
              <div style="margin-bottom: 8px;">
                <span style="font-weight: bold; color: #374151;">Department:</span>
                <span style="margin-left: 10px; color: #1f2937;">${employeeData?.department?.name || 'Loading...'}</span>
              </div>
              <div style="margin-bottom: 8px;">
                <span style="font-weight: bold; color: #374151;">Monthly Salary:</span>
                <span style="margin-left: 10px; color: #1f2937;">${formatCurrency(employeeData?.monthly_salary || 0)}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Attendance Summary -->
        ${payrollTotals ? `
        <div style="background: #dcfce7; padding: 20px; border-radius: 8px; margin-bottom: 20px;">
          <h3 style="color: #166534; margin: 0 0 15px 0; font-size: 18px;">Attendance Summary</h3>
          <div style="display: grid; grid-template-columns: repeat(4, 1fr); gap: 15px;">
            <div style="text-align: center;">
              <div style="font-size: 14px; color: #374151;">Total Days</div>
              <div style="font-size: 18px; font-weight: bold; color: #1e40af;">${payrollTotals.totalDays || 0}</div>
            </div>
            <div style="text-align: center;">
              <div style="font-size: 14px; color: #374151;">Weekend Days</div>
              <div style="font-size: 18px; font-weight: bold; color: #6b7280;">${payrollTotals.weekendDays || 0}</div>
            </div>
            <div style="text-align: center;">
              <div style="font-size: 14px; color: #374151;">Present Days</div>
              <div style="font-size: 18px; font-weight: bold; color: #166534;">${payrollTotals.presentDays}</div>
            </div>
            <div style="text-align: center;">
              <div style="font-size: 14px; color: #374151;">Missing Days</div>
              <div style="font-size: 18px; font-weight: bold; color: #ef4444;">${payrollTotals.missingDays}</div>
            </div>
            <div style="text-align: center;">
              <div style="font-size: 14px; color: #374151;">Absent Days</div>
              <div style="font-size: 18px; font-weight: bold; color: #dc2626;">${combinedAbsentDays}</div>
            </div>
            <div style="text-align: center;">
              <div style="font-size: 14px; color: #374151;">Leave Days</div>
              <div style="font-size: 18px; font-weight: bold; color: #1e40af;">${combinedLeaveDays}</div>
            </div>
            <div style="text-align: center;">
              <div style="font-size: 14px; color: #374151;">Late Days</div>
              <div style="font-size: 18px; font-weight: bold; color: #ea580c;">${payrollTotals.lateDays}</div>
            </div>
            <div style="text-align: center;">
              <div style="font-size: 14px; color: #374151;">Holiday Days</div>
              <div style="font-size: 18px; font-weight: bold; color: #7c3aed;">${payrollTotals.holidayDays || 0}</div>
            </div>
            <div style="text-align: center;">
              <div style="font-size: 14px; color: #374151;">Early Out</div>
              <div style="font-size: 18px; font-weight: bold; color: #db2777;">${payrollTotals.earlyOutDays || 0}</div>
            </div>
            <div style="text-align: center;">
              <div style="font-size: 14px; color: #374151;">Overtime Hours</div>
              <div style="font-size: 18px; font-weight: bold; color: #9333ea;">${formatHours(payrollTotals.totalOvertime)}</div>
            </div>
            <div style="text-align: center;">
              <div style="font-size: 14px; color: #374151;">Working Hours</div>
              <div style="font-size: 18px; font-weight: bold; color: #2563eb;">${formatHours(payrollTotals.totalWorkingHours)}</div>
            </div>
          </div>
        </div>
        ` : ''}

        <!-- Payment Details -->
        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 20px;">
          <!-- Allowances -->
          <div style="background: #dcfce7; padding: 20px; border-radius: 8px;">
            <h3 style="color: #166534; margin: 0 0 15px 0; font-size: 18px;">PAYMENT & ALLOWANCES</h3>
            ${allowances.map(allowance => `
              <div style="display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #bbf7d0;">
                <span style="color: #374151;">${allowance.name}</span>
                <span style="font-weight: bold; color: #166534;">${formatCurrency(allowance.amount)}</span>
              </div>
            `).join('')}
            <div style="display: flex; justify-content: space-between; padding: 12px 0; border-top: 2px solid #22c55e; margin-top: 10px;">
              <span style="font-weight: bold; color: #166534;">Total Allowances</span>
              <span style="font-size: 18px; font-weight: bold; color: #166534;">${formatCurrency(totalAllowances)}</span>
            </div>
          </div>

          <!-- Deductions -->
          <div style="background: #fee2e2; padding: 20px; border-radius: 8px;">
            <h3 style="color: #dc2626; margin: 0 0 15px 0; font-size: 18px;">DEDUCTIONS</h3>
            ${deductions.map(deduction => `
              <div style="display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #fecaca;">
                <span style="color: #374151;">${deduction.name}</span>
                <span style="font-weight: bold; color: #dc2626;">${formatCurrency(deduction.amount)}</span>
              </div>
            `).join('')}
            <div style="display: flex; justify-content: space-between; padding: 12px 0; border-top: 2px solid #ef4444; margin-top: 10px;">
              <span style="font-weight: bold; color: #dc2626;">Total Deductions</span>
              <span style="font-size: 18px; font-weight: bold; color: #dc2626;">${formatCurrency(totalDeductions)}</span>
            </div>
          </div>
        </div>

        <!-- Net Salary -->
        <div style="background: #dbeafe; padding: 20px; border-radius: 8px; text-align: center;">
          <div style="display: flex; justify-content: space-between; align-items: center;">
            <span style="font-size: 20px; font-weight: bold; color: #1e40af;">NET SALARY</span>
            <span style="font-size: 28px; font-weight: bold; color: #1e40af;">${formatCurrency(netSalary)}</span>
          </div>
        </div>

        <!-- Footer -->
        <div style="margin-top: 10px; text-align: center; color: #6b7280; font-size: 12px; border-top: 1px solid #e5e7eb; padding-top: 20px;">
          <p>Generated on ${getCurrentDate().format('DD/MM/YYYY HH:mm')}</p>
          <p>https://empellohrm.com</p>
           <p>Computer-generated — no signature/Stamp required. | Confidential — do not share.</p>
        </div>
      </div>
    `;
  };

  const formatCurrency = (amount) => {
    if (!amount) return "0.00";
    return new Intl.NumberFormat('en-IN', {
      style: 'decimal',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const formatHours = (hours) => {
    if (!hours || hours === 0) return "0h 0m";
    const wholeHours = Math.floor(hours);
    const minutes = Math.round((hours - wholeHours) * 60);
    return `${wholeHours}h ${minutes}m`;
  };

  const payrollTotals = calculatePayroll();
  const combinedLeaveDays = (payrollTotals?.leaveDays || 0) + (payrollTotals?.halfDays || 0);
  const combinedAbsentDays = (payrollTotals?.absentDays || 0) + (payrollTotals?.incompleteDays || 0);

  // Calculate tax and bonus allowances and deductions
  const calculateTaxAndBonusItems = () => {
    if (!taxAndBonusData || !employeeData) return { allowances: [], deductions: [] };
    
    const allowances = [];
    const deductions = [];
    const proportionalSalary = calculateProportionalSalary();
    
    taxAndBonusData.forEach(entry => {
      let amount = 0;
      
      if (entry.amountType === 'price') {
        // Fixed amount - apply proportion if employee joined mid-month
        if (employeeData?.joining_date) {
          const joiningDate = parseDate(employeeData.joining_date);
          const targetMonth = salaryStatus ? getCurrentDate() : getCurrentDate().subtract(1, 'month');
          
          if (joiningDate && joiningDate.isSame(targetMonth, 'month') && joiningDate.isSame(targetMonth, 'year')) {
            const effectiveDates = getEffectiveMonthDates();
            const totalMonthDates = getMonthDates();
            const proportion = effectiveDates.length / totalMonthDates.length;
            amount = entry.amount * proportion;
          } else {
            amount = entry.amount;
          }
        } else {
          amount = entry.amount;
        }
      } else if (entry.amountType === 'percentage') {
        // Percentage of proportional salary
        amount = (proportionalSalary * entry.amount) / 100;
      }
      
      if (entry.type === 'allowances') {
        allowances.push({
          name: entry.title,
          amount: amount
        });
      } else if (entry.type === 'deductions') {
        deductions.push({
          name: entry.title,
          amount: amount
        });
      } else if (entry.type === 'contributions') {
        deductions.push({
          name: entry.title,
          amount: amount
        });
      }
    });
    
    return { allowances, deductions };
  };

  // Calculate attendance-based deductions
  const calculateAttendanceDeductions = () => {
    if (!payrollTotals) return [];
    
    const deductions = [];
    
    // Missing days deduction - calculate based on per-day salary
    if (payrollTotals.missingDays > 0) {
      const perDaySalary = calculatePerDaySalary();
      const missingDeduction = payrollTotals.missingDays * perDaySalary;
      deductions.push({ name: "Missing Days Deduction", amount: missingDeduction });
    }
    
    // Absent days deduction
    if (payrollTotals.absentDays > 0) {
      const perDaySalary = calculatePerDaySalary();
      let absentDeduction = payrollTotals.absentDays * perDaySalary;
      
      // Apply proportion if employee joined mid-month
      if (employeeData?.joining_date) {
        const joiningDate = parseDate(employeeData.joining_date);
        const targetMonth = salaryStatus ? getCurrentDate() : getCurrentDate().subtract(1, 'month');
        
        if (joiningDate && joiningDate.isSame(targetMonth, 'month') && joiningDate.isSame(targetMonth, 'year')) {
          const effectiveDates = getEffectiveMonthDates();
          const totalMonthDates = getMonthDates();
          const proportion = effectiveDates.length / totalMonthDates.length;
          absentDeduction = absentDeduction * proportion;
        }
      }
      
      deductions.push({ name: "Absent Days Deduction", amount: absentDeduction });
    }

    // Late and Early Out deductions (already calculated in payroll totals)
    if (payrollTotals.totalDeductions > 0) {
      deductions.push({ name: "Late & Early Out Deductions", amount: payrollTotals.totalDeductions });
    }
    
    return deductions;
  };

  // Pakistan Tax Calculation Function
  const calculatePakistanTax = (monthlySalary) => {
    const annualSalary = monthlySalary * 12;
    let tax = 0;

    if (annualSalary <= 600000) {
      tax = 0;
    } else if (annualSalary <= 1200000) {
      tax = (annualSalary - 600000) * 0.01;
    } else if (annualSalary <= 2200000) {
      tax = 6000 + (annualSalary - 1200000) * 0.11;
    } else if (annualSalary <= 3200000) {
      tax = 6000 + 110000 + (annualSalary - 2200000) * 0.23;
    } else if (annualSalary <= 4100000) {
      tax = 6000 + 110000 + 230000 + (annualSalary - 3200000) * 0.30;
    } else if (annualSalary <= 5000000) {
      tax = 6000 + 110000 + 230000 + 270000 + (annualSalary - 4100000) * 0.35;
    } else if (annualSalary <= 6000000) {
      tax = 6000 + 110000 + 230000 + 270000 + 315000 + (annualSalary - 5000000) * 0.40;
    } else if (annualSalary <= 7000000) {
      tax = 6000 + 110000 + 230000 + 270000 + 315000 + 400000 + (annualSalary - 6000000) * 0.45;
    } else if (annualSalary <= 8000000) {
      tax = 6000 + 110000 + 230000 + 270000 + 315000 + 400000 + 450000 + (annualSalary - 7000000) * 0.50;
    } else if (annualSalary <= 9000000) {
      tax = 6000 + 110000 + 230000 + 270000 + 315000 + 400000 + 450000 + 500000 + (annualSalary - 8000000) * 0.55;
    } else if (annualSalary <= 10000000) {
      tax = 6000 + 110000 + 230000 + 270000 + 315000 + 400000 + 450000 + 500000 + 550000 + (annualSalary - 9000000) * 0.60;
    } else if (annualSalary <= 11000000) {
      tax = 6000 + 110000 + 230000 + 270000 + 315000 + 400000 + 450000 + 500000 + 550000 + 600000 + (annualSalary - 10000000) * 0.65;
    } else {
      tax = 6000 + 110000 + 230000 + 270000 + 315000 + 400000 + 450000 + 500000 + 550000 + 600000 + 650000 + (annualSalary - 11000000) * 0.70;
    }

    return {
      annualTax: tax,
      monthlyTax: tax / 12
    };
  };

  // Calculate income tax
  const calculateIncomeTax = () => {
    if (!employeeData?.monthly_salary) return 0;
    
    const monthlySalary = employeeData.monthly_salary;
    const { monthlyTax } = calculatePakistanTax(monthlySalary);
    
    // Apply proportion if employee joined mid-month
    if (employeeData?.joining_date) {
      const joiningDate = parseDate(employeeData.joining_date);
      const targetMonth = salaryStatus ? getCurrentDate() : getCurrentDate().subtract(1, 'month');
      
      if (joiningDate && joiningDate.isSame(targetMonth, 'month') && joiningDate.isSame(targetMonth, 'year')) {
        const effectiveDates = getEffectiveMonthDates();
        const totalMonthDates = getMonthDates();
        const proportion = effectiveDates.length / totalMonthDates.length;
        return monthlyTax * proportion;
      }
    }
    
    return monthlyTax;
  };

  // Calculate loan deductions
  const calculateLoanDeductions = () => {
    if (!loanData || !Array.isArray(loanData) || loanData.length === 0) return [];
    
    const deductions = [];
    
    loanData.forEach(loan => {
      if (loan.status === 'active' && loan.perMonthInstalment > 0) {
        deductions.push({
          name: `Loan Payment - ${loan.loanDate ? parseDate(loan.loanDate).format('MMM YYYY') : 'Loan'}`,
          amount: loan.perMonthInstalment
        });
      }
    });
    
    return deductions;
  };

  // Calculate remaining loan balance
  const calculateRemainingBalance = (loan) => {
    if (!loan || !loan.loanAmount) return 0;
    
    const totalPaid = loan.paidInstalments && Array.isArray(loan.paidInstalments) 
      ? loan.paidInstalments.reduce((sum, payment) => sum + (payment.amount || 0), 0)
      : 0;
    
    return Math.max(0, loan.loanAmount - totalPaid);
  };

  // Get tax and bonus items
  const { allowances: taxAndBonusAllowances, deductions: taxAndBonusDeductions } = calculateTaxAndBonusItems();

  // Allowances based on processed attendance and tax/bonus data
  const allowances = [
    { name: "Basic Salary", amount: calculateProportionalSalary() },
    { name: "Overtime Pay", amount: payrollTotals?.totalOvertimeSalary || 0 },
    ...taxAndBonusAllowances
  ];

  const attendanceDeductions = calculateAttendanceDeductions();
  const loanDeductions = calculateLoanDeductions();
  const incomeTax = calculateIncomeTax();
  
  // Add income tax to deductions if it's greater than 0
  const allDeductions = [...attendanceDeductions, ...loanDeductions, ...taxAndBonusDeductions];
  if (incomeTax > 0) {
    allDeductions.push({
      name: "Income Tax (Pakistan)",
      amount: incomeTax
    });
  }
  
  const deductions = allDeductions;

  const totalAllowances = allowances.reduce((sum, item) => sum + item.amount, 0);
  const totalDeductions = deductions.reduce((sum, item) => sum + item.amount, 0);
  const netSalary = totalAllowances - totalDeductions;

  if (loading) {
    return <div>Loading payslip data...</div>;
  }

  return (
    <>
      {/* Tabs */}
      <div className="flex border-b border-gray-200 mb-4">
        <button
          className={`px-4 py-2 text-sm font-medium ${
            activeTab === 'current'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
          onClick={() => setActiveTab('current')}
        >
          Current
        </button>
        <button
          className={`px-4 py-2 text-sm font-medium ${
            activeTab === 'previous'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
          onClick={() => setActiveTab('previous')}
        >
          Previous
        </button>
      </div>

      {/* Current Month Tab */}
      {activeTab === 'current' && (
        <table className="min-w-full bg-white ">
          <thead>
            <tr className="text-left border-b border-gray-200">
              <th className="px-4 py-2 text-[14px] font-semibold text-primary">
                Payslip Period
              </th>
              <th className="px-4 py-2 text-[14px] font-semibold text-primary text-right">
                Action
              </th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-gray-200">
              <td className="px-4 py-2 text-[14px] text-gray-700">
                {getTargetMonth()}
              </td>
              <td className="px-4 py-2 text-[14px] text-primary text-right">
                <div className="flex items-center gap-1 justify-end">
                  <Info 
                    className="h-3 w-3 text-blue-600 cursor-pointer" 
                    onClick={handleDetailsClick}
                    title="View Details"
                  />
                  <ArrowDown 
                    className={`h-3 w-3 cursor-pointer ${isGeneratingPDF ? 'text-gray-400' : 'text-black hover:text-blue-600'}`}
                    onClick={handleDownloadPDF}
                    title={isGeneratingPDF ? "Generating PDF..." : "Download PDF"}
                    style={{ pointerEvents: isGeneratingPDF ? 'none' : 'auto' }}
                  />
                  <EyeIcon
                    className="h-3 w-3 text-black cursor-pointer"
                    onClick={handleViewClick}
                  />
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      )}

      {/* Previous Month Tab */}
      {activeTab === 'previous' && (
        <div>
          {previousMonthLoading ? (
            <div className="text-center py-4">
              <div className="text-sm text-gray-500">Loading previous month data...</div>
            </div>
          ) : previousMonthData && previousMonthData.payrollRecords && previousMonthData.payrollRecords.length > 0 ? (
            <table className="min-w-full bg-white ">
              <thead>
                <tr className="text-left border-b border-gray-200">
                  <th className="px-4 py-2 text-[14px] font-semibold text-primary">
                    Payslip Period
                  </th>
                  <th className="px-4 py-2 text-[14px] font-semibold text-primary text-right">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-gray-200">
                  <td className="px-4 py-2 text-[14px] text-gray-700">
                    {getPreviousMonthInfo().name}
                  </td>
                  <td className="px-4 py-2 text-[14px] text-primary text-right">
                    <div className="flex items-center gap-1 justify-end">
                      <ArrowDown 
                        className={`h-3 w-3 cursor-pointer ${isGeneratingPDF ? 'text-gray-400' : 'text-black hover:text-blue-600'}`}
                        onClick={handleDownloadPreviousMonthPDF}
                        title={isGeneratingPDF ? "Generating PDF..." : "Download PDF"}
                        style={{ pointerEvents: isGeneratingPDF ? 'none' : 'auto' }}
                      />
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          ) : (
            <div className="text-center py-4">
              <div className="text-sm text-gray-500">No previous month data available</div>
            </div>
          )}
        </div>
      )}

      {/* Details Modal */}
      {detailsModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40"
          onClick={handleCloseDetailsModal}
        >
          <div
            className="bg-white rounded-lg shadow-lg p-6"
            style={{
              width: "95vw",
              height: "90vh",
              minWidth: "300px",
              maxWidth: "95vw",
              display: "flex",
              flexDirection: "column",
            }}
            onClick={e => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4 flex-shrink-0">
              <h2 className="text-lg font-semibold text-primary">
                Attendance Details - {getTargetMonth()}
              </h2>
              <button
                className="text-gray-500 hover:text-gray-700 text-xl font-bold"
                onClick={handleCloseDetailsModal}
                aria-label="Close"
              >
                &times;
              </button>
            </div>

            <div className="flex-1 overflow-y-auto pr-2">
              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Daily Attendance Log</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full bg-white border border-gray-200">
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Date</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Day</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Status</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Check In</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Check Out</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Working Hours</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Overtime</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Per Day Salary</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Overtime Salary</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Deductions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {processedAttendance.map((day, index) => (
                        <tr key={index} className="border-b border-gray-200 hover:bg-gray-50">
                          <td className="px-3 py-2 text-sm text-gray-900 border-b">
                            {formatDate(day.date, 'DD/MM/YYYY')}
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-900 border-b">
                            {formatDate(day.date, 'dddd')}
                          </td>
                          <td className="px-3 py-2 text-sm border-b">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              day.status === 'Present' ? 'bg-green-100 text-green-800' :
                              day.status === 'Late' ? 'bg-orange-100 text-orange-800' :
                              day.status === 'Missing' ? 'bg-red-100 text-red-800' :
                              day.status === 'Weekend' ? 'bg-gray-100 text-gray-800' :
                              day.status === 'Incomplete' ? 'bg-yellow-100 text-yellow-800' :
                              day.status === 'Early Out' ? 'bg-purple-100 text-purple-800' :
                              day.status === 'Late & Early Out' ? 'bg-pink-100 text-pink-800' :
                              day.status === 'Leave' ? 'bg-blue-100 text-blue-800' :
                              day.status === 'Holiday' ? 'bg-purple-100 text-purple-800' :
                              'bg-blue-100 text-blue-800'
                            }`}>
                              {day.status}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-900 border-b">
                            {formatTime(day.checkIn)}
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-900 border-b">
                            {formatTime(day.checkOut)}
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-900 border-b">
                            {day.workingHour || '0:00'}
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-900 border-b">
                            {day.overtime || '0:00'}
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-900 border-b">
                            {formatCurrency(day.perDaySelery || 0)}
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-900 border-b">
                            {formatCurrency(day.overtimeSalery || 0)}
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-900 border-b">
                            {day.status === 'Missing' 
                              ? formatCurrency(day.perDaySelery || 0)
                              : day.status === 'Late & Early Out'
                              ? formatCurrency((day.lateDeduction || 0) + (day.earlyOutDeduction || 0))
                              : formatCurrency(day.lateDeduction || day.incompleteDeduction || day.earlyOutDeduction || 0)
                            }
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {isModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40"
          onClick={handleCloseModal}
        >
          <div
            className="bg-white rounded-lg shadow-lg p-6"
            style={{
              width: "90vw",
              height: "90vh",
              minWidth: "300px",
              maxWidth: "95vw",
              display: "flex",
              flexDirection: "column",
            }}
            onClick={e => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4 flex-shrink-0">
              <h2 className="text-lg font-semibold text-primary">
                Payslip - {getTargetMonth()}
              </h2>
              <button
                className="text-gray-500 hover:text-gray-700 text-xl font-bold"
                onClick={handleCloseModal}
                aria-label="Close"
              >
                &times;
              </button>
            </div>

            <div className="flex-1 overflow-y-auto pr-2">
              {/* Employee Details Section */}
              <div className="bg-blue-50 p-4 rounded-lg mb-6">
                <h3 className="text-lg font-semibold text-blue-800 mb-4">Employee Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-gray-600">Employee Name:</span>
                      <span className="text-sm font-semibold">
                        {employeeData ? `${employeeData.firstName} ${employeeData.lastName}` : 'Loading...'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-gray-600">Employee ID:</span>
                      <span className="text-sm font-semibold">{employeeData?.employee_code || 'Loading...'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-gray-600">Join Date:</span>
                      <span className="text-sm font-semibold">
                        {employeeData?.joining_date ? parseDate(employeeData.joining_date).format('DD/MM/YYYY') : 'Loading...'}
                      </span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-gray-600">Designation:</span>
                      <span className="text-sm font-semibold">{employeeData?.designation?.designationName || 'Loading...'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-gray-600">Department:</span>
                      <span className="text-sm font-semibold">{employeeData?.department?.name || 'Loading...'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-gray-600">Monthly Salary:</span>
                      <span className="text-sm font-semibold">{formatCurrency(employeeData?.monthly_salary || 0)}</span>
                    </div>
                  </div>
                </div>
                
                {/* Joining Date Note */}
                {employeeData?.joining_date && (() => {
                  const joiningDate = parseDate(employeeData.joining_date);
                  const targetMonth = salaryStatus ? getCurrentDate() : getCurrentDate().subtract(1, 'month');
                  
                  if (joiningDate && joiningDate.isSame(targetMonth, 'month') && joiningDate.isSame(targetMonth, 'year')) {
                    const effectiveDates = getEffectiveMonthDates();
                    const totalMonthDates = getMonthDates();
                    const proportion = ((effectiveDates.length / totalMonthDates.length) * 100).toFixed(1);
                    
                    return (
                      <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <div className="flex items-center gap-2">
                          <span className="text-yellow-600">⚠️</span>
                          <span className="text-sm text-yellow-800">
                            <strong>Proportional Salary:</strong> Employee joined on {joiningDate.format('DD/MM/YYYY')} (day {joiningDate.date()}). 
                            Salary calculated for {effectiveDates.length} out of {totalMonthDates.length} days ({proportion}% of monthly salary).
                          </span>
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>

              {/* Loan Information Section */}
              {loanData && loanData.length > 0 && (
                <div className="bg-yellow-50 p-4 rounded-lg mb-6">
                  <h3 className="text-lg font-semibold text-yellow-800 mb-4">Active Loans</h3>
                  <div className="space-y-3">
                    {loanData.map((loan, index) => (
                      <div key={index} className="bg-white p-3 rounded border border-yellow-200">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div className="space-y-1">
                            <div className="flex justify-between">
                              <span className="text-sm font-medium text-gray-600">Loan Date:</span>
                              <span className="text-sm font-semibold">
                                {loan.loanDate ? parseDate(loan.loanDate).format('DD/MM/YYYY') : 'N/A'}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-sm font-medium text-gray-600">Loan:</span>
                              <span className="text-sm font-semibold">{formatCurrency(loan.loanAmount || 0)}</span>
                            </div>
                          </div>
                          <div className="space-y-1">
                            <div className="flex justify-between">
                              <span className="text-sm font-medium text-gray-600">Installment:</span>
                              <span className="text-sm font-semibold">{formatCurrency(loan.perMonthInstalment || 0)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-sm font-medium text-gray-600">Recovery:</span>
                              <span className="text-sm font-semibold">
                                {formatCurrency(
                                  loan.paidInstalments && Array.isArray(loan.paidInstalments) 
                                    ? loan.paidInstalments.reduce((sum, payment) => sum + (payment.amount || 0), 0)
                                    : 0
                                )}
                              </span>
                            </div>
                          </div>
                          <div className="space-y-1">
                            <div className="flex justify-between">
                              <span className="text-sm font-medium text-gray-600">Duration:</span>
                              <span className="text-sm font-semibold">{loan.numberOfMonths || 0} months</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-sm font-medium text-gray-600">Status:</span>
                              <span className="text-sm font-semibold text-green-600">{loan.status || 'Active'}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-sm font-medium text-gray-600">Remaining:</span>
                              <span className="text-sm font-semibold text-red-600">{formatCurrency(calculateRemainingBalance(loan))}</span>
                            </div>
                          </div>
                        </div>
                        {loan.paidInstalments && Array.isArray(loan.paidInstalments) && loan.paidInstalments.length > 0 && (
                          <div className="mt-3 pt-3 border-t border-yellow-200">
                            <div className="flex justify-between items-center mb-2">
                              <span className="text-sm font-medium text-gray-600">Paid:</span>
                              <span className="text-sm font-semibold text-green-600">
                                {loan.paidInstalments.length} of {loan.numberOfMonths || 0}
                              </span>
                            </div>
                            <div className="space-y-1 max-h-20 overflow-y-auto">
                              {loan.paidInstalments.map((payment, paymentIndex) => (
                                <div key={paymentIndex} className="flex justify-between text-xs text-gray-600">
                                  <span>{payment.date ? parseDate(payment.date).format('DD/MM/YYYY') : 'N/A'}</span>
                                  <span>{formatCurrency(payment.amount || 0)}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Attendance Summary */}
              {payrollTotals && (
                <div className="bg-green-50 p-4 rounded-lg mb-6">
                  <h3 className="text-lg font-semibold text-green-800 mb-4">Attendance Summary</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                    <div className="text-center">
                      <p className="text-sm text-gray-600">Total Days</p>
                      <p className="text-lg font-bold text-blue-600">
                        {payrollTotals.totalDays || 0}
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-gray-600">Weekend Days</p>
                      <p className="text-lg font-bold text-gray-500">{payrollTotals.weekendDays || 0}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-gray-600">Present Days</p>
                      <p className="text-lg font-bold text-green-600">{payrollTotals.presentDays}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-gray-600">Missing Days</p>
                      <p className="text-lg font-bold text-red-500">{payrollTotals.missingDays}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-gray-600">Absent Days</p>
                      <p className="text-lg font-bold text-red-600">{combinedAbsentDays}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-gray-600">Leave Days</p>
                      <p className="text-lg font-bold text-blue-600">{combinedLeaveDays}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-gray-600">Late Days</p>
                      <p className="text-lg font-bold text-orange-600">{payrollTotals.lateDays}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-gray-600">Holiday Days</p>
                      <p className="text-lg font-bold text-purple-600">{payrollTotals.holidayDays}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-gray-600">Early Out</p>
                      <p className="text-lg font-bold text-pink-600">{payrollTotals.earlyOutDays || 0}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-gray-600">Overtime Hours</p>
                      <p className="text-lg font-bold text-orange-600">{formatHours(payrollTotals.totalOvertime)}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-gray-600">Working Hours</p>
                      <p className="text-lg font-bold text-purple-600">{formatHours(payrollTotals.totalWorkingHours)}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Payment & Allowances and Deductions Section - Side by Side */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                {/* Payment & Allowances Section */}
                <div className="bg-green-50 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold text-green-800 mb-4">PAYMENT & ALLOWANCES</h3>
                  <div className="space-y-2">
                    {allowances.map((allowance, index) => (
                      <div key={index} className="flex justify-between items-center py-2 border-b border-green-200">
                        <span className="text-sm text-gray-700">{allowance.name}</span>
                        <span className="text-sm font-semibold text-green-700">{formatCurrency(allowance.amount)}</span>
                      </div>
                    ))}
                    <div className="flex justify-between items-center py-2 border-t-2 border-green-400">
                      <span className="text-sm font-bold text-green-800">Total Allowances</span>
                      <span className="text-lg font-bold text-green-800">{formatCurrency(totalAllowances)}</span>
                    </div>
                  </div>
                </div>

                {/* Deductions Section */}
                <div className="bg-red-50 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold text-red-800 mb-4">DEDUCTIONS</h3>
                  <div className="space-y-2">
                    {deductions.map((deduction, index) => (
                      <div key={index} className="flex justify-between items-center py-2 border-b border-red-200">
                        <span className="text-sm text-gray-700">{deduction.name}</span>
                        <span className="text-sm font-semibold text-red-700">{formatCurrency(deduction.amount)}</span>
                      </div>
                    ))}
                    <div className="flex justify-between items-center py-2 border-t-2 border-red-400">
                      <span className="text-sm font-bold text-red-800">Total Deductions</span>
                      <span className="text-lg font-bold text-red-800">{formatCurrency(totalDeductions)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Net Salary Section */}
              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-bold text-blue-800">NET SALARY</span>
                  <span className="text-2xl font-bold text-blue-800">{formatCurrency(netSalary)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Hidden div for PDF generation */}
      <div ref={pdfRef} style={{ display: 'none' }}></div>
    </>
  );
};

export default PaySlip;